import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/lib/i18n';

export interface CurrencySettings {
  code: string;
  symbol: string;
  name_ar: string;
  name_en: string;
}

// Common currencies for selection
export const CURRENCIES = [
  { code: 'EGP', symbol: 'ج.م', name_ar: 'جنيه مصري', name_en: 'Egyptian Pound' },
  { code: 'SAR', symbol: 'ر.س', name_ar: 'ريال سعودي', name_en: 'Saudi Riyal' },
  { code: 'AED', symbol: 'د.إ', name_ar: 'درهم إماراتي', name_en: 'UAE Dirham' },
  { code: 'KWD', symbol: 'د.ك', name_ar: 'دينار كويتي', name_en: 'Kuwaiti Dinar' },
  { code: 'QAR', symbol: 'ر.ق', name_ar: 'ريال قطري', name_en: 'Qatari Riyal' },
  { code: 'BHD', symbol: 'د.ب', name_ar: 'دينار بحريني', name_en: 'Bahraini Dinar' },
  { code: 'OMR', symbol: 'ر.ع', name_ar: 'ريال عماني', name_en: 'Omani Rial' },
  { code: 'JOD', symbol: 'د.أ', name_ar: 'دينار أردني', name_en: 'Jordanian Dinar' },
  { code: 'LBP', symbol: 'ل.ل', name_ar: 'ليرة لبنانية', name_en: 'Lebanese Pound' },
  { code: 'IQD', symbol: 'د.ع', name_ar: 'دينار عراقي', name_en: 'Iraqi Dinar' },
  { code: 'LYD', symbol: 'د.ل', name_ar: 'دينار ليبي', name_en: 'Libyan Dinar' },
  { code: 'TND', symbol: 'د.ت', name_ar: 'دينار تونسي', name_en: 'Tunisian Dinar' },
  { code: 'DZD', symbol: 'د.ج', name_ar: 'دينار جزائري', name_en: 'Algerian Dinar' },
  { code: 'MAD', symbol: 'د.م', name_ar: 'درهم مغربي', name_en: 'Moroccan Dirham' },
  { code: 'USD', symbol: '$', name_ar: 'دولار أمريكي', name_en: 'US Dollar' },
  { code: 'EUR', symbol: '€', name_ar: 'يورو', name_en: 'Euro' },
  { code: 'GBP', symbol: '£', name_ar: 'جنيه إسترليني', name_en: 'British Pound' },
];

const DEFAULT_CURRENCY: CurrencySettings = {
  code: 'EGP',
  symbol: 'ج.م',
  name_ar: 'جنيه مصري',
  name_en: 'Egyptian Pound',
};

export function useCurrency() {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const { data: settings, isLoading } = useQuery({
    queryKey: ['currency-settings'],
    queryFn: async () => {
      // Use RPC function to bypass RLS for public settings
      const { data, error } = await supabase.rpc('get_public_store_settings');
      
      if (error) throw error;
      
      // The RPC returns an array, get the first row
      const row = Array.isArray(data) ? data[0] : data;
      
      if (row) {
        const currencyData = {
          code: row.currency_code || DEFAULT_CURRENCY.code,
          symbol: row.currency_symbol || DEFAULT_CURRENCY.symbol,
          name_ar: row.currency_name_ar || DEFAULT_CURRENCY.name_ar,
          name_en: row.currency_name_en || DEFAULT_CURRENCY.name_en,
        } as CurrencySettings;
        
        // Cache for pixel tracking
        try {
          sessionStorage.setItem('store_currency_code', currencyData.code);
        } catch {}
        
        return currencyData;
      }
      
      return DEFAULT_CURRENCY;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  const currency = settings || DEFAULT_CURRENCY;

  type CurrencyDisplay = 'symbol' | 'code' | 'name' | 'none';

  const formatNumber = (value: number) =>
    value.toLocaleString(isRTL ? 'ar-EG' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const getCurrencyLabel = (display: CurrencyDisplay) => {
    switch (display) {
      case 'name':
        return isRTL ? currency.name_ar : currency.name_en;
      case 'code':
        return currency.code;
      case 'symbol':
        return currency.symbol;
      case 'none':
      default:
        return '';
    }
  };

  const formatMoney = (amount: number, options?: { display?: CurrencyDisplay }) => {
    const display = options?.display ?? 'symbol';
    const label = getCurrencyLabel(display);
    const formattedNumber = formatNumber(amount);
    return label ? `${formattedNumber} ${label}` : formattedNumber;
  };

  // Backward compatible API (used across the app)
  const formatPrice = (
    price: number,
    options?: { showSymbol?: boolean; showName?: boolean; showCode?: boolean; display?: CurrencyDisplay },
  ) => {
    const display: CurrencyDisplay =
      options?.display ??
      (options?.showName
        ? 'name'
        : options?.showCode
          ? 'code'
          : options?.showSymbol === false
            ? 'none'
            : 'symbol');

    return formatMoney(price, { display });
  };

  // Replace legacy hardcoded currency text inside templates / saved strings
  const replaceCurrencyText = (text: string) => {
    if (!text) return text;

    return text
      .replace(/\bEGP\b/g, currency.code)
      .replace(/ج\.م/g, currency.symbol)
      .replace(/جنيه مصري/g, currency.name_ar)
      .replace(/Egyptian Pound/g, currency.name_en);
  };

  // Get currency display name
  const getCurrencyName = () => (isRTL ? currency.name_ar : currency.name_en);

  // Get currency symbol only
  const getCurrencySymbol = () => currency.symbol;

  // Get currency code only
  const getCurrencyCode = () => currency.code;

  return {
    currency,
    isLoading,
    formatPrice,
    formatMoney,
    replaceCurrencyText,
    getCurrencyName,
    getCurrencySymbol,
    getCurrencyCode,
  };
}
